import {
  applyToPoint,
  boundsOfPoints,
  cutAt,
  cutRegions,
  eraseAlong,
  flattened,
  invert,
  nearestSegment,
  nodeCommands,
  outlineStroke,
  partsOf,
  pathConversion,
  placeInParent,
  replaceWithPathOps,
  ROOT_ID,
  subpathsOfCommands,
  simplifyStroke,
  sweptRings,
  type Matrix,
  type Rect,
  type Subpath,
} from '@flobi/artwork-core';
import type { IconName } from '../chrome/icons';
import type { Point, RailButton, Tool, ToolContext } from './types';

/**
 * Cutting a shape: the eraser, the scissors and the knife.
 *
 * Three marks in one Illustrator flyout, and they belong together for a reason no other
 * grouping has: each of them ends with a different number of shapes than it began with.
 * Everything else in the app moves anchors about. These three take ground away, or divide
 * it, and that is a different kind of act.
 *
 * The eraser rubs out wherever the brush passes. The scissors cuts a path at a point, so a
 * ring becomes an arc. The knife is drawn across a shape and leaves the pieces either
 * side. Two of the three are arithmetic on AREAS rather than on anchors, which is why they
 * ask the renderer's own path library through `carve` instead of working it out here:
 * pieces that merely happen to line up are not a cut, and a shape put back together from
 * them shows every seam.
 */

type Mode = 'erase' | 'scissors' | 'knife';

const MODES: { id: Mode; icon: IconName; title: string }[] = [
  { id: 'erase', icon: 'eraser', title: 'Eraser — rub a shape away wherever the brush passes' },
  { id: 'scissors', icon: 'scissors', title: 'Scissors — press on an edge to cut the path there; a ring becomes an arc' },
  { id: 'knife', icon: 'knife', title: 'Knife — draw across a shape to cut it into pieces' },
];

/** Samples nearer than this to the last one say nothing about where the hand went. */
const MIN_STEP = 2;
/** How near a press has to land on a path for the scissors to take it, in screen pixels. */
const REACH = 8;

export function cutTool(): Tool {
  const settings = { brush: 40 };
  let mode: Mode = 'erase';
  let drawing = false;
  let trail: Point[] = [];
  let hover: Point | null = null;
  /** What the scissors is over, so it can say where a press would cut before it is made. */
  let aim: ReturnType<typeof aiming> = null;
  /** Where the last cut landed, kept until the pointer moves on. */
  let snipped: Point | null = null;

  const radiusOf = (ctx: ToolContext) => (settings.brush / 2) * ctx.unitsPerPixel;

  /**
   * Every drawn shape whose box meets a rectangle: what a cut might touch. Not export
   * slices (they were cut into paintless paths and deleted), and nothing hidden or locked,
   * by itself or by a group it sits in.
   */
  const around = (ctx: ToolContext, box: Rect): string[] => {
    const meets = (other: Rect | null | undefined) =>
      !!other && other.x <= box.x + box.width && other.x + other.width >= box.x && other.y <= box.y + box.height && other.y + other.height >= box.y;
    const out = new Set<string>();
    let uncuttable = false;
    /**
     * Paths something else rides — type on a curve, a Cloner strung along one — and so
     * not the cut's to touch unless they are chosen. Type on a curve hides its curve, and
     * the eraser cut it anyway, so the words fell flat or vanished (QA 2026-09-24,
     * cut-slice-measure-F10). Gathered only once a path is actually under the cut.
     */
    let ridden: Set<string> | null = null;
    const isRidden = (id: string) => {
      if (!ridden) {
        ridden = new Set();
        for (const one of ctx.scene.ids()) {
          const rider = ctx.node(one) as { type?: string; onPath?: { id?: string }; pathId?: string } | undefined;
          if (rider?.type === 'text' && rider.onPath?.id) ridden.add(rider.onPath.id);
          if (rider?.type === 'cloner' && rider.pathId) ridden.add(rider.pathId);
        }
      }
      return ridden.has(id) && !ctx.selection.includes(id);
    };
    // Into groups and into artboards drawn as one layer: the index hands back whole units,
    // and a group has no outline to cut, so grouped art could never be cut at all
    // (QA 2026-09-24, cut-slice-measure-F7).
    const take = (id: string): void => {
      const node = ctx.node(id);
      if (!node || ctx.scene.hiddenHere(id) || ctx.scene.lockedHere(id)) return;
      if (node.type === 'artboard' || node.type === 'group') {
        for (const kid of ctx.scene.childrenOf(id)) if (meets(ctx.boundsOf(kid))) take(kid);
        return;
      }
      if (node.type === 'boolean' || node.type === 'cloner' || node.type === 'blend') {
        uncuttable = true;
        return;
      }
      if (ctx.scene.choosable(id) && node.type !== 'text' && node.type !== 'image' && !isRidden(id)) out.add(id);
    };
    for (const id of ctx.nearby(box)) take(id);
    if (uncuttable && out.size === 0) ctx.notice('Combined shapes, Cloners and Blends can’t be cut — expand them first.');
    return [...out];
  };

  /** What a node was wearing, so its pieces come out looking like what they came from. */
  /**
   * Everything the shape wears that a path can wear too. Cutting used to carry five
   * fields, so every piece lost the shape's effects, blend mode, mask role and animation.
   */
  const dressed = (ctx: ToolContext, id: string) => {
    const node = ctx.node(id) as (Record<string, unknown> & { name: string }) | undefined;
    if (!node) return null;
    const look: Record<string, unknown> = {};
    for (const key of ['name', 'visible', 'locked', 'opacity', 'isMask', 'animation', 'timing', 'fills', 'strokes', 'strokeStyle', 'effects', 'blendMode']) {
      if (node[key] !== undefined) look[key] = node[key];
    }
    return { look, parent: ctx.scene.parentOf(id) ?? ROOT_ID };
  };

  /** A line, an open stroke, or an outline with no fill: ink along a line, not an area. */
  const isLineArt = (ctx: ToolContext, id: string): boolean => {
    const node = ctx.node(id) as (NonNullable<ReturnType<ToolContext['node']>> & { fills?: { visible?: boolean }[] }) | undefined;
    if (!node) return false;
    if (node.type === 'line') return true;
    if (node.type === 'path' && node.path?.subpaths?.some((sub) => !sub.closed)) return true;
    return !(node.fills ?? []).some((paint) => paint.visible !== false);
  };

  /**
   * The eraser on line art: rubbed out along the line, the rest kept as open strokes.
   * True when it dealt with the shape (changed it, removed it, or found it untouched).
   */
  const rubLine = (ctx: ToolContext, id: string, rings: readonly (readonly Point[])[]): boolean => {
    const node = ctx.node(id);
    const commands = node ? nodeCommands(node) : null;
    if (!node || !commands || !pathConversion(ctx.scene, id).ok) return false;
    const toLocal = invert(ctx.scene.worldTransform(id));
    if (!toLocal) return false;
    const local = rings.map((ring) => ring.map((at) => applyToPoint(toLocal, at.x, at.y)));
    // A quarter of the brush, measured in the shape's own units.
    const spacing = (radiusOf(ctx) / 4) * Math.hypot(toLocal[0]!, toLocal[1]!);
    const runs = eraseAlong(subpathsOfCommands([...commands]), local, spacing);
    if (!runs) return true;
    if (runs.length === 0) {
      ctx.edit([{ op: 'delete', id }]);
      return true;
    }
    const ops = replaceWithPathOps(ctx.scene, id, { fillRule: node.type === 'path' ? (node.path?.fillRule ?? 'nonzero') : 'nonzero', subpaths: runs });
    if (ops) ctx.edit(ops);
    return true;
  };

  /**
   * Pieces written back as paths where the original was, and the original taken away.
   *
   * In world coordinates with the node's own transform undone, because the pieces came
   * from arithmetic done on the page and a path stores its points in its own space. Each
   * piece starts at its own first anchor, so a piece dragged off on its own carries a
   * sensible place rather than the corner of a box it no longer belongs to.
   */
  const replace = (ctx: ToolContext, id: string, pieces: Subpath[][]): string[] => {
    const worn = dressed(ctx, id);
    if (!worn) return [];
    const real = pieces.filter((piece) => piece.length > 0);
    if (real.length === 0) return [];
    const parentWorld = worn.parent === ROOT_ID ? null : ctx.scene.worldTransform(worn.parent);
    /**
     * A path keeps its id on its first piece, changed in place, as `replaceWithPathOps`
     * does: text riding it and a Cloner strung along it name it by that id, and new ids
     * for every piece left them riding nothing (cut-slice-measure-F10).
     */
    const inPlace = ctx.node(id)?.type === 'path';
    const ops: unknown[] = real.map((subpaths, index) => {
      // Through the parent's whole transform, so pieces inside a rotated group stay put.
      const placed = placeInParent(subpaths, parentWorld);
      if (inPlace && index === 0) return { op: 'set', id, props: { transform: placed.transform, path: { subpaths: placed.subpaths } } };
      return {
        op: 'create',
        ref: `piece${index}`,
        parentId: worn.parent,
        // In its place in the stack, not on top of everything.
        position: { before: id },
        node: {
          type: 'path',
          ...worn.look,
          transform: placed.transform,
          path: { subpaths: placed.subpaths },
        },
      };
    });
    const result = ctx.edit(inPlace ? ops : [...ops, { op: 'delete', id }]);
    if (!result.ok) {
      console.warn('Cutting failed', result.errors);
      return [];
    }
    return real.map((_, index) => (inPlace && index === 0 ? id : result.created[`piece${index}`]!)).filter(Boolean);
  };

  /**
   * An area cut into the parts a person sees as separate: each outline with the holes
   * inside it. A band rubbed through a shape, or a curved knife cut, left two islands as
   * one layer that selected and moved together (QA 2026-09-24, cut-slice-measure-F12).
   */
  const islands = (subpaths: Subpath[]): Subpath[][] => {
    if (subpaths.length < 2) return [subpaths];
    const parts = partsOf(subpaths.map((sub) => flattened(sub, 10)));
    return parts.length > 1 ? parts.map((part) => part.map((index) => subpaths[index]!)) : [subpaths];
  };

  /** The eraser: everything the brush did not cover, shape by shape. */
  const rub = (ctx: ToolContext, path: readonly Point[]): void => {
    const spine = simplifyStroke(path, Math.max(1, ctx.unitsPerPixel));
    const rings = sweptRings(spine, radiusOf(ctx));
    const box = boundsOfPoints(rings.flat());
    if (!box) return;
    const made: string[] = [];
    for (const id of around(ctx, box)) {
      if (isLineArt(ctx, id)) {
        rubLine(ctx, id, rings);
        continue;
      }
      // The brush as ONE area: asking every ring of a long scribble about every shape froze
      // the editor on release (QA 2026-09-24, cut-slice-measure-F11).
      const answer = ctx.carve(id, rings, { together: true });
      if (!answer) continue;
      // Nothing of it was under the brush, so it is not part of this at all.
      if (answer.outside.length > 0 && answer.inside.every((piece) => piece.length === 0)) continue;
      // The brush took the lot: the shape goes, and nothing takes its place.
      if (answer.outside.length === 0) {
        ctx.edit([{ op: 'delete', id }]);
        continue;
      }
      made.push(...replace(ctx, id, islands(answer.outside)));
    }
    if (made.length > 0) ctx.setSelection(made);
    else {
      // A chosen shape rubbed out entirely must leave the selection too: its id stayed
      // chosen, and the next Delete failed on it and deleted nothing (QA 2026-09-24,
      // cut-slice-measure-F20).
      const left = ctx.selection.filter((id) => ctx.node(id));
      if (left.length !== ctx.selection.length) ctx.setSelection(left);
    }
  };

  /** The knife: the two sides of the cut, shape by shape. */
  const slice = (ctx: ToolContext, path: readonly Point[]): void => {
    const spine = simplifyStroke(path, Math.max(1, ctx.unitsPerPixel));
    if (spine.length < 2) return;
    // A jitter is not a cut: a 1px stroke's heading used to be carried right across the
    // shape (QA 2026-09-24, cut-slice-measure-F19).
    let travelled = 0;
    for (let i = 1; i < spine.length; i++) travelled += Math.hypot(spine[i]!.x - spine[i - 1]!.x, spine[i]!.y - spine[i - 1]!.y);
    if (travelled / ctx.unitsPerPixel < 6) return;
    const box = boundsOfPoints(spine);
    if (!box) return;
    const made: string[] = [];
    for (const id of around(ctx, box)) {
      const bounds = ctx.boundsOf(id);
      if (!bounds) continue;
      const answer = ctx.carve(id, cutRegions(spine, bounds));
      if (!answer) continue;
      const pieces = answer.inside.filter((piece) => piece.length > 0);
      // One side had everything, so the cut missed: leave the shape as it was rather than
      // taking it apart and putting it back, which would cost an undo step for nothing.
      if (pieces.length < 2) continue;
      made.push(...replace(ctx, id, pieces.flatMap(islands)));
    }
    if (made.length > 0) ctx.setSelection(made);
  };

  /**
   * A shape's outline, whether it is stored as one or worked out from what it is.
   *
   * A rectangle keeps a width and a height, not a list of anchors, so there is nothing on
   * it to cut. The scissors reads its outline instead and hands back the pieces as paths
   * — which is the only honest answer, since a rectangle with a gap in one side is not a
   * rectangle any more.
   */
  const outlineOf = (ctx: ToolContext, id: string): Subpath[] => {
    const node = ctx.node(id);
    if (!node) return [];
    if (node.type === 'path') return (node.path?.subpaths ?? []) as Subpath[];
    const commands = nodeCommands(node);
    return commands ? subpathsOfCommands([...commands]) : [];
  };

  /** Everything a path's points need to be read on the page rather than in its own space. */
  const toWorldWith = (world2: Matrix) => (sub: Subpath): Subpath => ({
    closed: sub.closed,
    points: sub.points.map((at) => {
      const on = applyToPoint(world2, at.x, at.y);
      const bend = (handle: readonly [number, number] | undefined) => {
        if (!handle) return undefined;
        const moved = applyToPoint(world2, handle[0], handle[1]);
        return [moved.x, moved.y] as [number, number];
      };
      return { ...at, x: on.x, y: on.y, in: bend(at.in), out: bend(at.out) };
    }),
  });

  /**
   * Where a press would cut, if it were made here.
   *
   * Asked by the hover as well as by the press, so what is drawn under the pointer and
   * what actually happens cannot disagree. That was the whole trouble with this tool: it
   * cut in exactly the right place and said nothing about where that was, so pressing it
   * looked like pressing nothing.
   */
  const aiming = (
    ctx: ToolContext,
    world: Point,
  ): { id: string; which: number; index: number; t: number; at: Point; outline: Subpath[] } | null => {
    const reach = REACH * ctx.unitsPerPixel;
    const box = { x: world.x - reach, y: world.y - reach, width: reach * 2, height: reach * 2 };
    const candidates = around(ctx, box);
    if (candidates.length === 0) return null;
    // Where each sits in the stack, so of two outlines equally near the one on top is cut.
    // The index hands candidates back in no particular order, and the first found used to
    // win (QA 2026-09-24, cut-slice-measure-F13).
    const rank = new Map(candidates.map((id) => [id, -1]));
    if (candidates.length > 1) ctx.scene.paintOrder().forEach((id, index) => rank.has(id) && rank.set(id, index));
    let best: { id: string; which: number; index: number; t: number; at: Point; outline: Subpath[]; distance: number } | null = null;
    for (const id of candidates) {
      const subpaths = outlineOf(ctx, id);
      if (subpaths.length === 0) continue;
      // Measured on the page, not in the shape's own space: a path scaled up four times
      // was taken from four times the reach, and one scaled down only from very close
      // (QA 2026-09-24, cut-slice-measure-F14). A transform keeps a curve's parameters,
      // so the segment and t found here are the same ones on the stored path.
      const outline = subpaths.map(toWorldWith(ctx.scene.worldTransform(id)));
      for (let which = 0; which < outline.length; which++) {
        const subpath = outline[which]!;
        const found = nearestSegment(subpath.points, subpath.closed, world);
        if (!found || found.distance > reach) continue;
        const nearer = !best || found.distance < best.distance - ctx.unitsPerPixel / 2;
        const level = best && Math.abs(found.distance - best.distance) <= ctx.unitsPerPixel / 2;
        if (nearer || (level && rank.get(id)! > rank.get(best!.id)!)) {
          best = { id, which, index: found.index, t: found.t, at: found.at, outline, distance: found.distance };
        }
      }
    }
    if (!best) return null;
    const { distance: _distance, ...aim } = best;
    return aim;
  };

  /** The scissors: one path cut where the press landed. */
  const snip = (ctx: ToolContext, world: Point): boolean => {
    const aim = aiming(ctx, world);
    if (!aim) return false;
    {
      const { id, which } = aim;
      const subpaths = outlineOf(ctx, id);
      {
        const subpath = subpaths[which]!;
        const cut = cutAt(subpath.points, subpath.closed, aim.index, aim.t);
        // An open path pressed at its end is not cut: that press (a double-click, or one
        // just past the end of a line) used to leave a zero-length layer behind
        // (QA 2026-09-24, cut-slice-measure-F15).
        if (!subpath.closed && cut.length < 2) return false;
        // Each run becomes a path of its own. A cut that left one run — a ring opened —
        // stays one shape, because opening a ring does not make two of anything.
        const rest = subpaths.filter((_, index: number) => index !== which);
        const toWorld = toWorldWith(ctx.scene.worldTransform(id));
        const pieces = cut.map((run) => [toWorld(run), ...rest.map(toWorld)]);
        // Two runs: the shape's other parts (a hole, another island) stay with the first
        // piece. They were dropped, so a cut across one outline deleted the rest.
        const made = replace(ctx, id, cut.length > 1 ? cut.map((run, index) => (index === 0 ? [toWorld(run), ...rest.map(toWorld)] : [toWorld(run)])) : pieces.slice(0, 1));
        if (made.length > 0) ctx.setSelection(made);
        // Where it was cut, kept so the overlay can show it once the shape looks the same
        // as it did a moment ago — which, for a filled ring opened into an arc, it does.
        snipped = aim.at;
        return true;
      }
    }
  };

  const reset = () => {
    drawing = false;
    trail = [];
  };

  return {
    id: 'cut',

    deactivate(ctx) {
      reset();
      hover = null;
      aim = null;
      snipped = null;
      ctx.setCursor(null);
    },

    onPointerDown(_event, world, ctx) {
      if (mode === 'scissors') {
        snipped = null;
        if (snip(ctx, world)) ctx.stopCapturing();
        aim = null;
        ctx.refresh();
        return true;
      }
      drawing = true;
      trail = [world];
      return true;
    },

    onPointerMove(event, world, ctx) {
      hover = world;
      if (mode === 'scissors') {
        const found = aiming(ctx, world);
        const moved = (found?.id ?? null) !== (aim?.id ?? null) || (found ? Math.hypot(found.at.x - (aim?.at.x ?? 1e9), found.at.y - (aim?.at.y ?? 1e9)) > 0.01 : false);
        aim = found;
        ctx.setCursor(found ? 'crosshair' : null);
        if (moved || !found) ctx.refresh();
        return;
      }
      if (!drawing || event.buttons === 0) {
        if (mode === 'erase') ctx.refresh();
        return;
      }
      const last = trail[trail.length - 1];
      if (last && Math.hypot(world.x - last.x, world.y - last.y) < MIN_STEP * ctx.unitsPerPixel) return;
      trail.push(world);
      ctx.refresh();
    },

    onPointerUp(_event, world, ctx) {
      if (!drawing) return;
      const path = [...trail, world];
      reset();
      if (mode === 'erase') rub(ctx, path);
      else slice(ctx, path);
      ctx.stopCapturing();
      ctx.refresh();
    },

    /** Escape lets go of a knife or eraser stroke, so nothing is cut on release (QA 2026-09-24, cut-slice-measure-F24). */
    onKeyDown(event, ctx) {
      if (event.key !== 'Escape' || !drawing) return false;
      event.preventDefault();
      reset();
      ctx.refresh();
      return true;
    },

    /**
     * The three above, and how wide the eraser is below.
     *
     * The scissors has no quantity — it cuts where you press or it does not — and the
     * knife's line is its width, so only one of the three shows a well at all.
     */
    rail(ctx) {
      const items: RailButton[] = MODES.map((item) => ({
        id: item.id,
        icon: item.icon,
        title: item.title,
        pressed: mode === item.id,
        run: () => {
          mode = item.id;
          reset();
          aim = null;
          snipped = null;
          ctx.railChanged();
        },
      }));
      const top = { kind: 'buttons' as const, items };
      if (mode !== 'erase') return { top };
      return {
        top,
        bottom: {
          kind: 'slider' as const,
          icon: 'opacity' as const,
          title: 'Eraser size',
          value: settings.brush,
          min: 4,
          max: 200,
          onChange: (value: number) => {
            settings.brush = value;
            ctx.railChanged();
          },
        },
      };
    },

    /**
     * What the hand is about to take away, while it is taking it.
     *
     * The eraser shows its brush, because a brush you cannot see is a brush you cannot
     * aim. The knife shows the line it has drawn so far, because a cut is a line and the
     * whole question is where it falls.
     */
    draw(overlay, ctx) {
      if (mode === 'scissors') {
        const reach = 5 * ctx.unitsPerPixel;
        // The line it is about to cut, traced, so there is no doubt which of two shapes
        // under the pointer will come apart.
        if (aim) {
          for (const sub of aim.outline) {
            const run = sub.points.map((at) => ({ at, ...(at.in ? { in: { x: at.in[0], y: at.in[1] } } : {}), ...(at.out ? { out: { x: at.out[0], y: at.out[1] } } : {}) }));
            overlay.curve(sub.closed && run.length > 1 ? [...run, run[0]!] : run, { width: 1 });
          }
          // A cross on the line, at the exact place: the tool is a pair of scissors and
          // the one thing it has to tell you is where the blades are.
          overlay.line({ x: aim.at.x - reach, y: aim.at.y - reach }, { x: aim.at.x + reach, y: aim.at.y + reach }, { width: 2 });
          overlay.line({ x: aim.at.x - reach, y: aim.at.y + reach }, { x: aim.at.x + reach, y: aim.at.y - reach }, { width: 2 });
        }
        // Where the last cut landed, and the loose ends it left. A filled ring opened into
        // an arc looks exactly as it did a moment ago, so without these a cut that worked
        // perfectly is indistinguishable from a press that did nothing.
        if (snipped) overlay.knob(snipped, { hovered: true });
        for (const id of ctx.selection) {
          const node = ctx.node(id);
          if (node?.type !== 'path') continue;
          const matrix = ctx.scene.worldTransform(id);
          for (const sub of node.path?.subpaths ?? []) {
            if (sub.closed || sub.points.length < 2) continue;
            for (const end of [sub.points[0]!, sub.points[sub.points.length - 1]!]) {
              overlay.handle(applyToPoint(matrix, end.x, end.y), { filled: true });
            }
          }
        }
        return;
      }
      if (mode === 'knife' && trail.length > 1) {
        overlay.curve(
          trail.map((at) => ({ at })),
          { width: 1 },
        );
        return;
      }
      if (mode !== 'erase' || !hover) return;
      const radius = radiusOf(ctx);
      const steps = 32;
      const ring = Array.from({ length: steps + 1 }, (_, step) => {
        const angle = (step / steps) * Math.PI * 2;
        return { at: { x: hover!.x + Math.cos(angle) * radius, y: hover!.y + Math.sin(angle) * radius } };
      });
      overlay.curve(ring, { width: 1 });
      // The ground about to go, as ONE silhouette. Outlining each swept piece separately
      // drew forty overlapping circles and rectangles down the stroke, which says "here
      // is some machinery" rather than "this is what you are rubbing out".
      if (trail.length > 1) {
        const swept = outlineStroke(simplifyStroke(trail, Math.max(1, ctx.unitsPerPixel)), radius);
        if (swept.length > 2) overlay.curve([...swept, swept[0]!].map((at) => ({ at })), { width: 1 });
      }
    },
  };
}
